import React, { useRef, useState } from 'react'
import { useFormikContext } from 'formik'
import { useParams } from 'react-router-dom'
import { Upload, RotateCcw } from 'lucide-react'
import { Modal, Radio } from 'antd'

import { Button } from 'components/ui/button'
import { toast } from 'components/ui/use-toast'
import requests from 'service/api'
import { asRows } from 'lib/asyncList'
import {
  normalizeZaraOrderJson,
  zaraJsonToFormFields,
  mirrorPricesToLegacyMix,
  applyQuantitiesToFormSizes,
  buildQrCacheItems,
  applyQrCacheResults,
  checkSectionMatchesDepartment,
  detectOverwriteTargets,
  matchSizeGroupBySerie,
  normalizeCompanyName,
  padZaraColorCode
} from 'lib/zaraOrderImport'
import { zaraSizeName } from 'lib/matchCatalogSizes'

/**
 * Referans Kodu value formatı (SelectReferanceCode3 → ReferanceAsyncSelect):
 *   valueField = 'id' → Formik priceLabel[idx].referanceCode = ReferanceCode record ID'si
 *   select label = item.code (örn. "HPZCALL004")
 * Bu yüzden importer HPZCALL004'ü code bazlı resolve edip record ID'sini yazar.
 * Backend /list/referance?code=... exact findFirst destekler (labelCategory filtresiz).
 */
const REFERENCE_LABEL_CATEGORY = 'FİYAT KARTI'

/**
 * "Zara JSON'dan Doldur" butonu — SADECE NORMAL ZARA formunda render edilir.
 * TopSection brand !== 'ZaraHome' koşuluyla bu component'i basar.
 *
 * Import akışı (deterministic):
 *  1. dosya oku → JSON parse
 *  2. normalizeZaraOrderJson: base schema + brand safety + component normalize
 *  3. section/department safety check
 *  4. Company resolve (normalizeCompanyName, exact-1 → auto select)
 *  5. SizeGroup resolve (JSON sizeSerie set == group set, exact-1 → auto select)
 *  6. legacy priceLabel[0] merge (renk, beden template korunur)
 *  7. backend QR cache çağrısı → newPriceLabel qr alanları tamamlanır
 *  8. Formik alanları tek kontrollü "transaction" gibi set edilir
 *  9. Beden qty'leri sizeGroup fetch bittikten sonra race-safe uygulanır
 */
export default function ZaraJsonImportButton({ deptName }) {
  const { values, setFieldValue } = useFormikContext()
  const { dept } = useParams()
  const departmentId = dept?.split('_')[0]
  const departmentLabel = dept?.split('_')[1] // örn. "Man", "Woman"

  const fileInputRef = useRef(null)
  const [importing, setImporting] = useState(false)
  // sizeGroup fetch'i async olduğu için qty'leri sonra uygulamak için saklanır.
  // MULTI-COLOR: priceLabel index (renk) bazlı pending map.
  // { [priceLabelIndex]: { variant, applied } }
  const pendingQuantitiesByColorRef = useRef({})

  /* ---------------------- Company resolution ---------------------- */
  async function resolveCompany(supplierName) {
    try {
      // Mevcut Company lookup API'sini kullan — tümünü çekip client-side normalize eşleştir
      const rows = await asRows(requests.company.list({ limit: 50 }))
      // limit 50'yi aşabilir; supplier adına search de yap
      const searched = await asRows(
        requests.company.list({ search: String(supplierName).slice(0, 20), limit: 20 })
      )
      const all = [...rows, ...searched]
      const target = normalizeCompanyName(supplierName)

      const exactMatches = []
      const seenIds = new Set()
      for (const c of all) {
        if (seenIds.has(c.id)) continue
        seenIds.add(c.id)
        if (normalizeCompanyName(c.name) === target) exactMatches.push(c)
      }

      if (exactMatches.length === 1) {
        return { companyId: exactMatches[0].id, warning: null }
      }
      if (exactMatches.length === 0) {
        return {
          companyId: null,
          warning: `Müşteri bulunamadı: "${supplierName}". Lütfen manuel seçiniz.`
        }
      }
      return {
        companyId: null,
        warning: `Birden fazla müşteri eşleşti (${exactMatches
          .map((c) => c.name)
          .join(', ')}). Lütfen manuel seçiniz.`
      }
    } catch (e) {
      return {
        companyId: null,
        warning: `Müşteri sorgulanamadı: ${e?.message || 'bilinmeyen hata'}. Manuel seçiniz.`
      }
    }
  }

  /* ---------------------- SizeGroup resolution ---------------------- */
  /**
   * Departmanın TÜM beden tablolarını (detaylarıyla) getirir.
   * Otomatik seçim YAPILMAZ — hangi tabloların JSON serisiyle eşleştiği
   * işaretlenir ve öneri (suggestion) hesaplanır; seçim her zaman kullanıcıya
   * aittir (chooseSizeGroupInteractively modal'ı).
   */
  async function resolveSizeGroup(newPriceLabel) {
    const serie = newPriceLabel?.article?.sizeSerie || []
    if (serie.length === 0) {
      return { groups: [], suggestion: null, warning: null }
    }

    try {
      const groups = await asRows(requests.size.listGroup(departmentId, { limit: 50 }))
      if (!Array.isArray(groups) || groups.length === 0) {
        return { groups: [], suggestion: null, warning: null }
      }

      // Tüm grupların size detaylarını parallel çek
      const detailed = await Promise.all(
        groups.map(async (g) => {
          try {
            const { data } = await requests.size.getGroup(g.id)
            return { id: g.id, name: g.name, sizes: data?.sizes || [] }
          } catch {
            return { id: g.id, name: g.name, sizes: null }
          }
        })
      )

      // Grup bazlı kapsam kontrolü: JSON serisinin TÜM üyeleri tabloda var mı?
      // (matchSizeGroupBySerie ile aynı kurallar: europe yoksa sCode, trim'li)
      const norm = (s) => String(s ?? '').trim()
      const targetSet = new Set(serie.map(norm))

      const enriched = detailed.map((g) => {
        const sizes = Array.isArray(g.sizes) ? g.sizes : []
        const visible = new Set(
          sizes
            .map((s) => norm(s?.europe != null ? s.europe : s?.sCode ?? ''))
            .filter(Boolean)
        )
        let coversAll = true
        for (const t of targetSet) {
          if (!visible.has(t)) {
            coversAll = false
            break
          }
        }
        return { ...g, matches: coversAll }
      })

      // Öneri: algoritmanın tek adaya indiği durumda o aday; yoksa ilk
      // eşleşen grup; hiçbiri eşleşmiyorsa null (modal yine açılır, seçim
      // serbest — kullanıcı JSON serisinden FARKLI bir tablo da seçebilir).
      const { match } = matchSizeGroupBySerie(serie, detailed)
      const suggestion = match
        ? { id: match.id, name: match.name, sizes: match.sizes || [] }
        : enriched.find((g) => g.matches) || null

      // Eşleşenler önce görünsün
      enriched.sort((a, b) => Number(b.matches) - Number(a.matches))

      return { groups: enriched, suggestion, warning: null }
    } catch (e) {
      return {
        groups: [],
        suggestion: null,
        warning: `Beden tabloları sorgulanamadı: ${e?.message || 'bilinmeyen hata'}`
      }
    }
  }

  /* ---------------------- SizeGroup kullanıcı seçimi ---------------------- */
  /**
   * Departmandaki TÜM beden tablolarını listeler; kullanıcı seçer.
   * JSON serisiyle eşleşen tablolar "(JSON serisiyle eşleşiyor)" etiketiyle
   * ve listenin başında gösterilir; varsa öneri ön-seçilir.
   * "Onayla" → seçilen {id,name,sizes}; "İptal" → null (import iptal edilir).
   */
  function chooseSizeGroupInteractively(serie, groups, suggestion) {
    return new Promise((resolve) => {
      let selectedId = suggestion?.id ?? groups[0]?.id ?? null // default: öneri

      Modal.confirm({
        title: 'Beden tablosu seçin',
        width: 520,
        okText: 'Seç ve Doldur',
        cancelText: 'İptal',
        content: (
          <div>
            <p style={{ marginBottom: 12 }}>
              JSON beden serisi: <b>({serie.join(', ')})</b>. Bu sipariş için
              kullanılacak beden tablosunu seçin:
            </p>
            <div style={{ maxHeight: 300, overflowY: 'auto', paddingRight: 4 }}>
              <Radio.Group
                defaultValue={selectedId}
                onChange={(e) => {
                  selectedId = e.target.value
                }}
                style={{ display: 'flex', flexDirection: 'column', gap: 8 }}
              >
                {groups.map((g) => (
                  <Radio key={g.id} value={g.id}>
                    {g.name}
                    {g.matches && (
                      <span
                        style={{
                          marginLeft: 8,
                          fontSize: 11,
                          color: '#16a34a',
                          fontWeight: 600
                        }}
                      >
                        ✓ JSON serisiyle eşleşiyor
                      </span>
                    )}
                  </Radio>
                ))}
              </Radio.Group>
            </div>
            <p style={{ marginTop: 12, fontSize: 12, color: '#888' }}>
              Seçim onaylanınca beden satırları ve miktarlar seçilen tabloya
              göre otomatik doldurulur. İptal ederseniz içe aktarma durdurulur.
            </p>
          </div>
        ),
        onOk: () =>
          resolve(groups.find((g) => g.id === selectedId) || null),
        onCancel: () => resolve(null)
      })
    })
  }

  /* ---------------------- QR cache ---------------------- */
  async function cacheQrs(newPriceLabel) {
    const items = buildQrCacheItems(newPriceLabel)
    if (items.length === 0) return { newPriceLabel, warning: null }

    try {
      const { data } = await requests.zaraPriceLabel.cacheQr({ items })
      const results = data?.results || []
      const updated = applyQrCacheResults(newPriceLabel, results)

      const failed = results.filter((r) => r.cacheStatus === 'failed').length
      const warning =
        failed > 0 ? `${items.length} QR'dan ${failed} tanesi cache edilemedi.` : null
      return { newPriceLabel: updated, warning }
    } catch (e) {
      // QR fail olması import'u bozmaz — cacheStatus 'pending' kalır
      return {
        newPriceLabel,
        warning: `QR cache sırasında hata: ${e?.message || 'bilinmeyen'}. QR'lar sonra tekrar denenebilir.`
      }
    }
  }

  /* ---------------------- Referans Kodu resolution ---------------------- */
  /**
   * newPriceLabel.template.reference (örn. "HPZCALL004") → ReferanceCode record ID'si.
   *
   * SelectReferanceCode3 → ReferanceAsyncSelect valueField='id' kullanır;
   * Formik priceLabel[idx].referanceCode alanı record ID'si bekler.
   *
   * Resolution stratejisi (case farkı kabul edilir — hpzcall004 == HPZCALL004):
   *  1. /list/referance?code=X (backend exact findFirst) + client-side exact compare
   *  2. sonuç yoksa /list/referance?search=X (contains, insensitive) +
   *     client-side EXACT case-insensitive code eşleşmesi (fuzzy contains SEÇMEZ)
   *
   * Kurallar:
   *  - exact-1 kayıt → otomatik seç (id döner)
   *  - 0 kayıt → import fail ETMEZ, warning ver
   *  - >1 kayıt → otomatik seçme, warning ver
   *  - yeni ReferanceCode oluşturma
   *  - labelInstances UUID'leri ASLA referans kodu olarak yazılmaz
   */
  async function resolveReferenceCode(referenceCode) {
    if (!referenceCode) return { referanceCodeValue: null, warning: null }

    const target = String(referenceCode).trim().toLowerCase()
    const isExactMatch = (r) => String(r?.code || '').trim().toLowerCase() === target

    try {
      // 1) Backend exact code lookup (labelCategory filtresiyle)
      let rows = await asRows(
        requests.referance.list({ code: referenceCode, labelCategory: REFERENCE_LABEL_CATEGORY })
      )
      let matches = rows.filter(isExactMatch)

      // 2) Case-farklı giriş → insensitive search ile genişlet, yine EXACT compare
      if (matches.length === 0) {
        const searched = await asRows(
          requests.referance.list({
            search: referenceCode,
            labelCategory: REFERENCE_LABEL_CATEGORY,
            limit: 20
          })
        )
        rows = [...rows, ...searched]
        matches = rows.filter(isExactMatch)
      }

      if (matches.length === 1) {
        return { referanceCodeValue: matches[0].id, warning: null, matchedCode: matches[0].code }
      }
      if (matches.length === 0) {
        return {
          referanceCodeValue: null,
          warning: `${referenceCode} Referans Kodu sistemde bulunamadı. Lütfen manuel seçiniz.`
        }
      }
      return {
        referanceCodeValue: null,
        warning: `${referenceCode} referans kodu birden fazla kayıtla eşleşti. Otomatik select yapılmadı, lütfen manuel seçiniz.`
      }
    } catch (e) {
      return {
        referanceCodeValue: null,
        warning: `Referans kodu sorgulanamadı: ${e?.message || 'bilinmeyen hata'}. Manuel seçiniz.`
      }
    }
  }

  /* ---------------------- Ana import handler ---------------------- */
  async function handleFile(file) {
    setImporting(true)
    const warnings = []

    try {
      /* 1-2. Dosya oku + JSON parse + minimum validation + normalize */
      const text = await file.text()
      let rawJson
      try {
        rawJson = JSON.parse(text)
      } catch (e) {
        toast.error(`Geçersiz JSON dosyası: ${e.message}`)
        return
      }

      const result = normalizeZaraOrderJson(rawJson)
      if (!result.ok) {
        toast.error(result.errors[0] || 'JSON doğrulaması başarısız.')
        return
      }
      let { data: newPriceLabel } = result
      result.warnings.forEach((w) => warnings.push(w))

      /* 3. Section / Department safety check */
      const sectionCheck = checkSectionMatchesDepartment(
        newPriceLabel.source.section,
        departmentLabel
      )
      if (!sectionCheck.ok) {
        toast.error(sectionCheck.reason)
        return
      }

      /* Formda dolu alan var mı? — validate bitti, mutate öncesi onay al */
      const overwriteTargets = detectOverwriteTargets(values)
      if (overwriteTargets.length > 0) {
        const confirmed = await new Promise((resolve) => {
          Modal.confirm({
            title: 'Formda mevcut veri var — üzerine yazılsın mı?',
            content: (
              <div>
                <p style={{ marginBottom: 8 }}>
                  Şu alanlar JSON verisiyle güncellenecek:
                </p>
                <ul style={{ paddingLeft: 18, margin: 0 }}>
                  {overwriteTargets.map((t, i) => (
                    <li key={i} style={{ marginBottom: 2 }}>
                      {t.label}
                    </li>
                  ))}
                </ul>
              </div>
            ),
            okText: 'Evet, üzerine yaz',
            cancelText: 'İptal',
            onOk: () => resolve(true),
            onCancel: () => resolve(false)
          })
        })
        if (!confirmed) return
      }

      /* 4. Company resolve */
      const company = await resolveCompany(newPriceLabel.source.supplier.name)
      if (company.warning) warnings.push(company.warning)

      /* 5. SizeGroup — otomatik seçim YOK: tüm tablolar listelenir,
       * kullanıcı seçer, seçimle doldurulur. İptal → import durur. */
      const sizeGroupResult = await resolveSizeGroup(newPriceLabel)
      if (sizeGroupResult.warning) warnings.push(sizeGroupResult.warning)

      let selectedSizeGroup = null
      if (sizeGroupResult.groups.length > 0) {
        selectedSizeGroup = await chooseSizeGroupInteractively(
          newPriceLabel.article.sizeSerie,
          sizeGroupResult.groups,
          sizeGroupResult.suggestion
        )
        if (!selectedSizeGroup) {
          // Kullanıcı iptal etti — form mutate EDİLMEDEN dur
          toast.info('Beden tablosu seçilmedi — içe aktarma iptal edildi.')
          return
        }
        warnings.push(`Beden tablosu "${selectedSizeGroup.name}" seçildi.`)
      } else {
        warnings.push('Departmanda beden tablosu bulunamadı — mevcut tablo korunuyor.')
      }

      const sizeGroup = { sizeGroupId: selectedSizeGroup?.id || null, chosenDetail: selectedSizeGroup }

      /* 5b. Referans Kodu resolve (labels[0].reference → ReferanceCode record ID) */
      const reference = await resolveReferenceCode(newPriceLabel.template?.reference)
      if (reference.warning) warnings.push(reference.warning)

      /* 6. MULTI-COLOR legacy priceLabel — "Renk Ekle" pattern'ini reuse et:
       *    push({ ...priceLabel[0], colorCode, sizeDefectivePercent: '', sizes: [template, amount'lar temizlenmiş] })
       *    Her JSON color → bir legacy entry; fiyatlar MODEL_QUALITY seviyesinde
       *    olduğundan tüm renklere aynı USA+UK mirror'ı uygulanır (BLACK YOK). */
      const templateEntry = values.priceLabel?.[0] || {}
      const baseSizes = Array.isArray(templateEntry.sizes)
        ? templateEntry.sizes
        : []
      const emptyAmountSizes = baseSizes.map((size) => ({
        ...size,
        amount: '',
        amountWithDefect: ''
      }))
      const sharedMix = mirrorPricesToLegacyMix(newPriceLabel, templateEntry.mix)

      const legacyPriceLabel = newPriceLabel.variants.map((variant) => {
        // "Renk Ekle" pattern'i: her yeni entry priceLabel[0] template'inden klonlanır
        const source = templateEntry
        return {
          ...source,
          colorCode: padZaraColorCode(variant.colorCode),
          // Ana label referansı label-type seviyesindedir — her renge aynı uygulanır.
          // (labelInstances UUID'leri newPriceLabel variant/size içinde kalır.)
          referanceCode: reference.referanceCodeValue || source.referanceCode || '',
          sizeDefectivePercent: '',
          sizes: emptyAmountSizes.map((s) => ({ ...s })),
          mix: sharedMix.map((m) => ({ ...m }))
        }
      })

      /* 7. QR cache (backend) */
      const qrResult = await cacheQrs(newPriceLabel)
      newPriceLabel = qrResult.newPriceLabel
      if (qrResult.warning) warnings.push(qrResult.warning)

      /* 8. Formik alanları tek kontrollü import transaction'ı gibi uygula */
      const formFields = zaraJsonToFormFields(newPriceLabel)

      setFieldValue('brandOrderNo', formFields.brandOrderNo)
      setFieldValue('supplierNo', formFields.supplierNo)
      setFieldValue('season', formFields.season)
      setFieldValue('articleNo', formFields.articleNo)

      if (company.companyId) {
        setFieldValue('company', company.companyId)
      }

      // MULTI-COLOR: tüm legacy priceLabel array'ini tek seferde yaz
      setFieldValue('priceLabel', legacyPriceLabel)

      // Beden tablosu: kullanıcı seçimi yapıldıysa
      if (sizeGroup.sizeGroupId) {
        /* 8b. Tablonun satırlarını DOĞRUDAN yaz — PriceSizes effect'i yalnızca
         * "Fiyat Etiketi" tab'ı mount'lıyken yaşar; import sırasında kullanıcı
         * başka tab'daysa satırlar hiç kurulmazdı. Seçim modal'ı grup detayını
         * (sizes dahil) döndürür — ekstra fetch gerekmez. PriceSizes aynı
         * veriyi uid bazlı idempotent yeniden kurduğu için çakışma olmaz. */
        const chosenDetail = sizeGroup.chosenDetail || null
        let groupSizes = chosenDetail?.sizes || null

        if (!Array.isArray(groupSizes)) {
          // Grup detayı elimizde değilse fetch et (savunmacı yol)
          try {
            const { data } = await requests.size.getGroup(sizeGroup.sizeGroupId)
            groupSizes = data?.sizes || []
          } catch {
            groupSizes = []
          }
        }

        const templateRows = (Array.isArray(groupSizes) ? groupSizes : []).map((item) => ({
          name: zaraSizeName(item, chosenDetail || {}),
          amount: '',
          amountWithDefect: '',
          cn: item.cn,
          cm: item.cm,
          uid: item.uid,
          sCode: item.sCode || (item.europe != null ? String(item.europe) : '')
        }))

        legacyPriceLabel.forEach((entry, i) => {
          if (templateRows.length > 0) {
            setFieldValue(`priceLabel[${i}].sizes`, templateRows.map((r) => ({ ...r })))
          }
        })

        const pendingMap = {}
        newPriceLabel.variants.forEach((variant, i) => {
          pendingMap[i] = { variant, applied: false }
        })
        pendingQuantitiesByColorRef.current = pendingMap
        setFieldValue('sizeGroup', sizeGroup.sizeGroupId)
      } else {
        // sizeGroup resolve edilemedi — mevcut satırlara map etmeyi dene (renk bazlı)
        pendingQuantitiesByColorRef.current = {}
        legacyPriceLabel.forEach((entry, i) => {
          const variant = newPriceLabel.variants[i]
          if (variant && Array.isArray(entry.sizes) && entry.sizes.length > 0) {
            const mapped = applyQuantitiesToFormSizes(entry.sizes, variant)
            setFieldValue(`priceLabel[${i}].sizes`, mapped)
          }
        })
      }

      // newPriceLabel'ı form state'e koy — save payload {...values} ile DB'ye gider
      setFieldValue('newPriceLabel', newPriceLabel)

      /* 9. Sonuç bildirimi */
      const okCount = warnings.length === 0
      if (okCount) {
        toast.success(
          `Zara JSON içe aktarıldı: ${newPriceLabel.article.articleNo} / ${newPriceLabel.source.campaign} (Toplam ${newPriceLabel.validation.calculatedOrderQty} adet)`
        )
      } else {
        Modal.warning({
          title: 'JSON içe aktarıldı — ancak uyarılar var',
          width: 620,
          content: (
            <ul style={{ paddingLeft: 18, margin: 0 }}>
              {warnings.map((w, i) => (
                <li key={i} style={{ marginBottom: 4 }}>
                  {w}
                </li>
              ))}
            </ul>
          )
        })
      }
    } catch (e) {
      toast.error(`İçe aktarma sırasında hata: ${e?.message || 'bilinmeyen hata'}`)
    } finally {
      setImporting(false)
    }
  }

  /* Race-safe qty uygulama: sizeGroup fetch'i bittiğinde pending qty'leri yaz.
   * MULTI-COLOR: her priceLabel index (renk) kendi pending girişinden bağımsız uygulanır.
   * Not: PriceSizes useEffect'ü her idx için AYRI çalışır ve sadece kendi
   * priceLabel[idx].sizes template'ini kurar — burada da per-idx izleriz. */
  React.useEffect(() => {
    const pendingMap = pendingQuantitiesByColorRef.current
    if (!pendingMap || Object.keys(pendingMap).length === 0) return

    Object.entries(pendingMap).forEach(([idxKey, pending]) => {
      if (!pending || pending.applied) return
      const idx = Number(idxKey)
      const sizes = values.priceLabel?.[idx]?.sizes
      if (!Array.isArray(sizes) || sizes.length === 0) return

      // Beden satırları sizeGroup template'inden mi doldu? (uid/europe işaretli)
      const fromTemplate = sizes.some((s) => s?.uid || s?.cn || s?.cm)
      if (!fromTemplate) return // PriceSizes effect bu idx için henüz çalışmadı — bekle

      // "1" ↔ "01" sıfır dolgusu: karşılaştırma anahtarları normalize edilir
      const norm = (v) => {
        const s = String(v ?? '').trim()
        return /^\d+$/.test(s) ? String(parseInt(s, 10)) : s
      }
      const qtyKeys = new Set(
        (pending.variant?.sizes || []).flatMap((s) => [
          String(s.size).trim(),
          norm(s.size)
        ])
      )
      const matched = sizes.filter(
        (row) =>
          qtyKeys.has(String(row?.sCode ?? '').trim()) ||
          qtyKeys.has(String(row?.name ?? '').trim()) ||
          qtyKeys.has(norm(row?.sCode)) ||
          qtyKeys.has(norm(row?.name))
      ).length
      if (matched === 0) return // template hazır ama hiç eşleşme yok — uygulanmaz

      pending.applied = true
      const mapped = applyQuantitiesToFormSizes(sizes, pending.variant)
      setFieldValue(`priceLabel[${idx}].sizes`, mapped)
    })
  }, [values.priceLabel, values.sizeGroup])

  function onInputChange(e) {
    const file = e.target.files?.[0]
    if (!file) return
    handleFile(file)
    // aynı dosyayı tekrar seçebilsin
    e.target.value = ''
  }

  return (
    <>
      <input
        ref={fileInputRef}
        type="file"
        accept=".json,application/json"
        className="hidden"
        onChange={onInputChange}
      />
      <Button
        type="button"
        variant="blue"
        disabled={importing}
        onClick={() => fileInputRef.current?.click()}
        className="whitespace-nowrap"
      >
        {importing ? (
          <RotateCcw className="animate-spin mr-2" size={16} />
        ) : (
          <Upload className="mr-2" size={16} />
        )}
        {importing ? 'İçe Aktarılıyor...' : "Zara JSON'dan Doldur"}
      </Button>
    </>
  )
}
